/**
 * ============================================================================
 * SESSION MANAGER COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Manage active sessions and device access
 */

import React, { useState, useEffect } from 'react';
import { useSecurity } from '../../hooks/useSecurity';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import Modal from '../common/Modal';
import StatusBadge from '../common/StatusBadge';
import EmptyState from '../common/EmptyState';

/**
 * Session Manager Component
 * @returns {JSX.Element}
 */
const SessionManager = () => {
  const {
    activeSessions,
    loadActiveSessions,
    revokeSession,
    revokeAllOtherSessions,
    loading,
  } = useSecurity();
  const { showSuccess, showError } = useToast();
  const { formatDate, formatTime, language } = useI18n();

  const [showRevokeAllModal, setShowRevokeAllModal] = useState(false);
  const [revokingSessionId, setRevokingSessionId] = useState(null);

  useEffect(() => {
    loadActiveSessions();
  }, [loadActiveSessions]);

  const handleRevokeSession = async (sessionId) => {
    try {
      setRevokingSessionId(sessionId);
      await revokeSession(sessionId);
      showSuccess(
        language === 'fr' ? 'Session révoquée' : 'Session revoked'
      );
      await loadActiveSessions();
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de révocation' : 'Failed to revoke session')
      );
    } finally {
      setRevokingSessionId(null);
    }
  };

  const handleRevokeAll = async () => {
    try {
      await revokeAllOtherSessions();
      showSuccess(
        language === 'fr' ? 'Toutes les autres sessions révoquées' : 'All other sessions revoked'
      );
      setShowRevokeAllModal(false);
      await loadActiveSessions();
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de révocation' : 'Failed to revoke sessions')
      );
    }
  };

  const getDeviceIcon = (userAgent) => {
    if (!userAgent) return '💻';
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return '📱';
    }
    if (ua.includes('tablet') || ua.includes('ipad')) {
      return '📱';
    }
    return '💻';
  };

  const getBrowserName = (userAgent) => {
    if (!userAgent) return 'Unknown';
    const ua = userAgent.toLowerCase();
    if (ua.includes('chrome')) return 'Chrome';
    if (ua.includes('firefox')) return 'Firefox';
    if (ua.includes('safari') && !ua.includes('chrome')) return 'Safari';
    if (ua.includes('edge')) return 'Edge';
    if (ua.includes('opera')) return 'Opera';
    return 'Unknown';
  };

  if (loading && !activeSessions) {
    return (
      <Card variant="glass" className="p-12 text-center">
        <Spinner size="lg" />
      </Card>
    );
  }

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-black text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-2xl">🔐</span>
            {language === 'fr' ? 'Sessions actives' : 'Active Sessions'}
          </h3>
          {activeSessions && activeSessions.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRevokeAllModal(true)}
            >
              {language === 'fr' ? 'Révoquer toutes' : 'Revoke All'}
            </Button>
          )}
        </div>

        {/* Sessions List */}
        {activeSessions && activeSessions.length > 0 ? (
          <div className="space-y-3">
            {activeSessions.map((session, index) => {
              const isCurrent = session.isCurrent || session.current;
              return (
                <Card
                  key={session.id || index}
                  variant="outlined"
                  className={`${
                    isCurrent
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                      : 'bg-gray-50 dark:bg-gray-800/50'
                  } animate-fadeInUp`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="text-3xl">{getDeviceIcon(session.userAgent)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-black text-gray-900 dark:text-white">
                            {getBrowserName(session.userAgent)} {isCurrent && '(Current)'}
                          </h4>
                          {isCurrent && (
                            <StatusBadge variant="success" size="sm">
                              {language === 'fr' ? 'Actuel' : 'Current'}
                            </StatusBadge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {session.ipAddress || session.ip} • {session.location || 'Unknown location'}
                        </p>
                        {session.lastActivity && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {language === 'fr' ? 'Dernière activité:' : 'Last activity:'}{' '}
                            {formatDate(session.lastActivity)} {formatTime(session.lastActivity)}
                          </p>
                        )}
                      </div>
                    </div>
                    {!isCurrent && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevokeSession(session.id)}
                        loading={revokingSessionId === session.id}
                        className="text-red-600 hover:text-red-700 dark:text-red-400"
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon="🔐"
            title={language === 'fr' ? 'Aucune session' : 'No active sessions'}
            description={
              language === 'fr'
                ? 'Vous n\'avez pas de sessions actives'
                : "You don't have any active sessions"
            }
          />
        )}

        {/* Info */}
        <Card variant="outlined" className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <p className="font-bold text-yellow-800 dark:text-yellow-200 mb-1">
                {language === 'fr' ? 'Sécurité' : 'Security'}
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                {language === 'fr'
                  ? 'Révoquez toute session que vous ne reconnaissez pas. Cela déconnectera l\'appareil immédiatement.'
                  : 'Revoke any session you don\'t recognize. This will immediately log out that device.'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Revoke All Modal */}
      <Modal
        isOpen={showRevokeAllModal}
        onClose={() => setShowRevokeAllModal(false)}
        title={
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚠️</span>
            <span className="text-xl font-black text-red-600 dark:text-red-400">
              {language === 'fr' ? 'Révoquer toutes les sessions' : 'Revoke All Sessions'}
            </span>
          </div>
        }
      >
        <div className="space-y-6">
          <Card variant="outlined" className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
            <p className="text-sm text-red-700 dark:text-red-300">
              {language === 'fr'
                ? 'Cela révoquera toutes les autres sessions actives. Vous resterez connecté sur cet appareil.'
                : 'This will revoke all other active sessions. You will remain logged in on this device.'}
            </p>
          </Card>
          <div className="flex gap-3">
            <Button
              variant="outline"
              fullWidth
              onClick={() => setShowRevokeAllModal(false)}
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              variant="gradient"
              fullWidth
              onClick={handleRevokeAll}
              loading={loading}
              className="bg-gradient-to-r from-red-600 to-pink-600 shadow-xl hover:shadow-2xl transition-all"
            >
              {language === 'fr' ? 'Confirmer' : 'Confirm'} →
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default SessionManager;
